"use client"

import { createContext, useContext, type ReactNode } from "react"
import { useCompany, useImpersonation } from "@/hooks/use-company"
import type { Company, CompanyContext } from "@/lib/types"

interface CompanyContextType {
  // Company management
  currentCompany: CompanyContext | null
  companies: Company[]
  switchCompany: (company: CompanyContext) => void
  clearCompany: () => void
  
  // Impersonation (for platform admins)
  isImpersonating: boolean
  impersonatedUserId: string | null
  startImpersonation: (userId: string) => void
  stopImpersonation: () => void
}

const CompanyContext = createContext<CompanyContextType>({
  currentCompany: null,
  companies: [],
  switchCompany: () => {},
  clearCompany: () => {},
  isImpersonating: false,
  impersonatedUserId: null,
  startImpersonation: () => {},
  stopImpersonation: () => {},
})

interface CompanyProviderProps {
  children: ReactNode
}

export function CompanyProvider({ children }: CompanyProviderProps) {
  const company = useCompany()
  const impersonation = useImpersonation()

  return (
    <CompanyContext.Provider 
      value={{
        ...company,
        ...impersonation,
      }}
    >
      {children}
    </CompanyContext.Provider>
  )
}

export function useCompanyContext() {
  return useContext(CompanyContext)
}